import { Pagination, Recipe } from '../../type';
import { Creator } from '../../type/creator';
import {
  Diary,
  NotiFlag,
  RecipeComment,
  RecipeCurationData,
} from '../../type/recipe';
import { deleteCall, get, patch, post } from '../axios';

import { CreateRecipeDto } from './dto/create-recipe.dto';
import {
  CreateRecipeCommentDto,
  CreateRecipeReplyDto,
} from './dto/create-recipe-comment.dto';
import { CreateRecipeCreatorDto } from './dto/create-recipe-creator.dto';
import { SearchRecipeDto } from './dto/search-recipes.dto';
import { UpdateRecipeCreatorDto } from './dto/update-recipe-creator.dto';

class GetAllRecipesOption {
  carousel?: boolean;
}

class CreateRecipeReviewDto {
  photos: string[];
  content: string;
}

export const getCarouselRecipes = (option?: GetAllRecipesOption) => {
  return get(`recipe/carousel`);
};

export type RecipeOrder = 'createdAt' | 'likeCount';
export const getRecipesByPagination = async (
  page = '',
  pageSize = '',
  orderBy: RecipeOrder = 'likeCount',
) => {
  const { data } = await get<Pagination<Recipe>>(
    `recipe?page=${page}&pageSize=${pageSize}&orderBy=${orderBy}`,
  );
  return data;
};

export const getOneRecipe = async (id: number) => {
  const { data } = await get<Recipe>(`recipe/${id}`);
  return data;
};

export const createRecipe = (data: Partial<Recipe>) => {
  return post('recipe', {
    name: data.name,
    description: data.description,
    thumbnail: data.images ? data.images[0] : '',
    cuisine: data.cuisine,
    category: data.category,
    mainIngredient: data.mainIngredient,
    subIngredient: data.subIngredient,
    recipeIngredient: data.recipeIngredient,
    steps: data.steps,
    tips: [],
    hashTags: data.hashTags,
    yield: data.yield,
    totalTime: data.totalTime,
  });
};

export const createRecipeByDto = async (createRecipeDto: CreateRecipeDto) => {
  return post<Recipe>('recipe', createRecipeDto);
};

export const updateRecipe = (id: number, data: Partial<Recipe>) => {
  return patch(`recipe/${id}`, data);
};

export const getAllRecipeReviews = (recipeId: number) => {
  return get(`recipe/${recipeId}/review`);
};

export const getRecipeComments = (recipeId: number, page = 1, pageSize = 5) => {
  return get<Pagination<RecipeComment>>(
    `recipe/${recipeId}/comment?page=${page}&pageSize=${pageSize}`,
  );
};

export const createRecipeCommentByDto = (
  createRecipeCommentDto: CreateRecipeCommentDto,
) => {
  return post(`recipe/comment`, createRecipeCommentDto);
};

export const editRecipeCommentByDto = (
  commentId: number,
  editRecipeCommentDto: Partial<CreateRecipeCommentDto>,
) => {
  return patch(`recipe/comment/${commentId}`, editRecipeCommentDto);
};

export const removeRecipeComment = (commentId: number) => {
  return deleteCall(`recipe/comment/${commentId}`);
};

export const createRecipeCommentLike = (commentId: number) => {
  return post(`recipe/comment/${commentId}/like`, '');
};

export const removeRecipeCommentLike = (commentId: number) => {
  return deleteCall(`recipe/comment/${commentId}/like`);
};

export const createRecipeReplyByDto = (
  createRecipeReplyDto: CreateRecipeReplyDto,
) => {
  return post(`recipe/comment`, createRecipeReplyDto);
};

export const createRecipeReview = (
  recipeId: number,
  data: CreateRecipeReviewDto,
) => {
  return post(`recipe/${recipeId}/review`, data);
};

export const searchRecipes = async ({
  q,
  ingredients,
  hashTags,
  time,
  isOfficialOnly,
  page,
  pageSize,
  orderBy,
}: SearchRecipeDto) => {
  let ingredientsQuery = '';
  let hashTagsQuery = '';
  let timeQuery = time ? `time=${time}` : '';
  let officialQuery = isOfficialOnly ? `isOfficialOnly=t` : '';
  let pageQuery = page ? `page=${page}` : '';
  let pageSizeQuery = pageSize ? `pageSize=${pageSize}` : 'pageSize=12';
  let orderByQuery = orderBy ? `orderBy=${orderBy}` : '';

  if (ingredients) {
    ingredientsQuery = ingredients
      .map((i) => `ingredients[]=${encodeURIComponent(i)}`)
      .join('&');
  }
  if (hashTags) {
    hashTagsQuery = hashTags
      .map((h) => `hashTags[]=${encodeURIComponent(h)}`)
      .join('&');
  }

  const queryString = [
    ingredientsQuery,
    hashTagsQuery,
    timeQuery,
    officialQuery,
    pageQuery,
    pageSizeQuery,
    orderByQuery,
  ]
    .filter((q) => q)
    .join('&');

  const { data } = await get<Pagination<Recipe>>(
    `recipe/search?q=${encodeURIComponent(q)}${
      queryString ? `&${queryString}` : ''
    }`,
  );
  return data;
};

export const getGeneralRecipeCurations = async () => {
  const { data } = await get<RecipeCurationData[]>(`recipe/curation`);
  return data;
};

/**
 * @link https://www.notion.so/GET-recipe-curation-name-efa27288eda84381be8e164c28a351cd?pvs=4
 */
export const getRecipeCurationByName = async (
  name: string,
  size: number = 6,
) => {
  const { data } = await get<RecipeCurationData>(
    `recipe/curation/${name}?size=${size}`,
  );
  return data;
};

export const getMyRecipe = () => {
  return get(`/recipe/me`);
};

export const getMainIngredient = async () => {
  const { data } = await get<string[]>(`/recipe/me/main-ingredient`);
  return data;
};

export const saveBookMark = (id: number) => {
  return post(`/recipe/${id}/bookmark`, '');
};

export const removeBookMark = (id: number) => {
  return deleteCall(`/recipe/${id}/bookmark`);
};

export const getMyRecipeBookmark = async () => {
  const { data } = await get<Recipe[]>(`/recipe/me/bookmark`);

  return data;
};

export const createRecipeCreator = (
  createRecipeCreatorDto: CreateRecipeCreatorDto,
) => {
  return post('/recipe/creator', createRecipeCreatorDto);
};

export const updateRecipeCreator = (
  updateRecipeCreatorDto: UpdateRecipeCreatorDto,
) => {
  return patch('/recipe/creator', updateRecipeCreatorDto);
};

export const checkRecipeCreatorName = async (name: string) => {
  const { data } = await get<Creator>(`/recipe/creator?name=${name}`);

  return data;
};

export const searchHashTag = async (q: string) => {
  const { data } = await get<string[]>(`/recipe/hashTag/search?q=${q}`);

  return data;
};

export const searchIngredients = async (q: string) => {
  const { data } = await get<string[]>(`/recipe/ingredient/search?q=${q}`);

  return data;
};

export const getAllIngredients = () => {
  return get('/recipe/ingredient');
};

export const updateRecipeByDto = (
  id: number,
  data: Partial<CreateRecipeDto>,
) => {
  return patch(`recipe/${id}`, data);
};

export const removeRecipe = (id: number) => {
  return deleteCall(`recipe/${id}`);
};

export const getRecipeLikeCount = (id: number) => {
  return get(`recipe/${id}/like`);
};

export const likeRecipe = (id: number) => {
  return post(`recipe/${id}/like`, '');
};

export const removeLikeRecipe = (id: number) => {
  return deleteCall(`recipe/${id}/like`);
};

export const getCreator = async () => {
  const { data } = await get<Creator>('recipe/me/creator');

  return data;
};

export const getDiariesReferencingRecipe = async (
  id: number,
  page = 1,
  pageSize = 10,
) => {
  const { data } = await get<Pagination<Diary>>(
    `recipe/${id}/diary?page=${page}&pageSize=${pageSize}`,
  );

  return data;
};

export const getMylikes = async (page = 1, pageSize = 20) => {
  const { data } = await get<Pagination<Recipe | Diary>>(
    `recipe/me/like?page=${page}&pageSize=${pageSize}`,
  );

  return data;
};

interface NotiFlags {
  [NotiFlag.UGLYUS_RECIPE]: boolean;
  [NotiFlag.SUBSCRIBER_COOKDIARY]: boolean;
  [NotiFlag.MY_DIARY]: boolean;
  [NotiFlag.RECIPE_PAPER]: boolean;
}
export const getMyNotiFlags = () => {
  return get<NotiFlags>('recipe/me/noti-flag');
};

interface UpdateNotiFlagDto extends Partial<NotiFlags> {}
export const updateNotiFlag = (arg: UpdateNotiFlagDto) => {
  return patch('recipe/me/noti-flag', arg);
};
